import {Component} from '@angular/core';
import {MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
import {MatListModule} from "@angular/material/list";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {RecordingType} from "@allaihealth/util";
import {MatIconModule} from "@angular/material/icon";
import {CommonModule} from '@angular/common';
import {MatFormField, MatFormFieldModule} from "@angular/material/form-field";
import {MatSelectModule} from '@angular/material/select';
import {NoteSection, RecordingStates} from '../../../lib/enums';
import {firstValueFrom} from "rxjs";
import {YesNoData, YesNoDialogComponent} from "../../../components/yes-no-dialog/yes-no-dialog.component";
import {PreventDoubleClickDirective} from "../../../lib/prevent-double-click.directive";
import {BaseRecordingComponent} from "../base-recording.component";
import {MatRippleModule} from "@angular/material/core";
import {SessionService} from "../../../services/session.service";

/** Show the dictation dialog */
@Component({
	selector: 'dictation-dialog',
	imports: [
		CommonModule,
		MatFormField,
		MatFormFieldModule,
		MatDialogModule,
		MatSelectModule,
		MatButtonModule,
		MatListModule,
		ReactiveFormsModule,
		MatIconModule,
		FormsModule,
		PreventDoubleClickDirective,
		MatRippleModule
	],
	templateUrl: './dictation-dialog.component.html',
	styleUrl: './dictation-dialog.component.scss'
})
export class DictationDialogComponent extends BaseRecordingComponent {
	readonly RecordingStates = RecordingStates;

	/** The selected category for the dictation */
	private _selectedNoteSection:NoteSection;

	/** All the note sections */
	readonly options:NoteSection[] = Object.values(NoteSection);

	/** Has the user started recording yet? */
	hasStarted:boolean = false;

	constructor(private dialogRef:MatDialogRef<DictationDialogComponent>,
	            private dialog:MatDialog,
	            private sessionService:SessionService) {
		super();
	}

	override onNewAppointmentData():Promise<void> {
		const userId = this.sessionService.allaiUser.id;
		this._selectedNoteSection = localStorage.getItem(`selectedNoteSection-${userId}`) as NoteSection;

		return super.onNewAppointmentData();
	}

	override startMic():Promise<void> {
		this.hasStarted = true;

		return super.startMic();
	}

	/** Close the dialog and cancel the recording */
	async closeDialog():Promise<void> {
		if(this.hasStarted) {
			this.pauseMic();

			const ref = this.dialog.open<YesNoDialogComponent, YesNoData, boolean>(
				YesNoDialogComponent, {
					data: {
						yes: "Yes",
						no: "No",
						title: "Are you sure?",
						message: "Cancelling now will discard any dictation you just recorded.",
						disableClose: true
					}
				});

			//wait for a result and if true cancel the recording and close
			if(await firstValueFrom(ref.afterClosed())) {
				this.cancelRecording();
				this.dialogRef.close();
				this.snackbar.message("Dictation deleted.");
			}
		} else {
			this.dialogRef.close();
		}
	}

	protected override getAudioData() {
		// create the request
		return {
			type: RecordingType.Dictation,
			category: this.getCategory()
		};
	}

	protected putAudioPartComplete():void {
		this.snackbar.message("Dictation saved. It will appear in the final note.");

		this.dialogRef.close();
	}

	/** Reset the recording process to the default (waiting or done) */
	protected override resetToDefaultState() {
		if(this.state != RecordingStates.Unsupported && this.state != RecordingStates.NoDevices) {
			this.state = RecordingStates.Initial;
		}
	}

	protected override onInterrupt() {
		this.cancelRecording();
		this.snackbar.error("Your dictation was interrupted and could not be saved. Please re-record.");
		this.state = RecordingStates.Initial;
		this.hasStarted = false;
	}

	getCategory():string {
		if(this.selectedNoteSection) {
			switch(this.selectedNoteSection) {
				case NoteSection.ChiefComplaint:
					return "cc";
				case NoteSection.Hpi:
					return "hpi";
				case NoteSection.PhysicalExam:
					return "pe";
				case NoteSection.Assessment:
					return "asmt";
				case NoteSection.Plan:
					return "pl";
			}

		}
		return null;
	}

	/** Get the selected category for the dictation */
	get selectedNoteSection() {
		return this._selectedNoteSection;
	}

	/** Set the selected category for the dictation */
	set selectedNoteSection(value:NoteSection) {
		this._selectedNoteSection = value;
		const userId = this.sessionService.allaiUser.id;
		if(value) {
			localStorage.setItem(`selectedNoteSection-${userId}`, this._selectedNoteSection);
		}
	}
}
